import { styled } from '@mui/material';

import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { ButtonName } from '../../theme/componentNames';
import { createResponsiveStyles } from '../../theme/util';

const QuantityControlContainer = styled('div', { label: 'QuantityControlContainer' })(({ theme }) => {
  const cssStyleOverrides = createResponsiveStyles(
    theme,
    theme.componentCSSOverrides?.[ButtonName.CheckoutQuantityControl],
  );
  const btnStyles = {
    backgroundColor: theme.vars.palette.tertiary.main,
    color: theme.vars.palette.tertiary.contrastText,
    ...cssStyleOverrides,
  };
  return {
    ...btnStyles,
    display: 'flex',
    width: 80,
    height: 23,
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 14,
    button: {
      padding: 0,
    },
    '.control': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: 'inherit',
      outline: 'none',
      border: 'none',
      color: 'inherit',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transitionProperty: 'transform',
      transitionDuration: '.12s',
      '&:active': {
        transform: 'scale(.9)',
      },
      '&.minus': {
        paddingLeft: 8,
        minWidth: 'unset',
        margin: 0,
        height: 'auto',
      },
      '&.add': {
        paddingRight: 8,
        minWidth: 'unset',
        margin: 0,
        height: 'auto',
      },
    },
    '.count': {
      cursor: 'default',
      fontSize: 'inherit',
      fontWeight: 600,
      flex: 1,
      textAlign: 'center',
      color: 'inherit',
    },
    '&.large': {
      width: 96,
      height: 30,
      fontSize: 14,
      '.control': {
        '&.minus': {
          paddingLeft: '10px',
        },
        '&.add': {
          paddingRight: '10px',
        },
      },
      [theme.breakpoints.down('sm')]: {
        width: 68,
        height: 27,
        '.control': {
          '&.minus': {
            paddingLeft: '6px',
          },
          '&.add': {
            paddingRight: '6px',
          },
        },
      },
    },
  };
});
export const QuantityControl = ({
  large = false,
  count,
  backgroundColor,
  onAddClick,
  onMinusClick,
  disabled = false,
  responsiveBackgroundColor,
}: {
  large?: boolean;
  count: number;
  onAddClick: React.MouseEventHandler<HTMLButtonElement>;
  onMinusClick: React.MouseEventHandler<HTMLButtonElement>;
  trackedParams?: Record<string, any>;
  disabled?: boolean;
  backgroundColor?: string;
  responsiveBackgroundColor?: string;
}) => {
  return (
    <QuantityControlContainer
      className={large ? 'large' : ''}
      sx={(theme) => ({
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // The buttons carry their own `cursor: pointer`, which would otherwise
        // out-specify the container's `not-allowed` while disabled.
        ...(disabled && { '.control': { cursor: 'not-allowed' } }),
        backgroundColor,
        [theme.breakpoints.down('sm')]: {
          backgroundColor: responsiveBackgroundColor || backgroundColor,
        },
      })}
      onClick={(e) => {
        if (disabled) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
    >
      <button onClick={(e) => onMinusClick(e)} className="minus control" aria-label="Decrement quantity">
        <RemoveRoundedIcon sx={{ fontSize: 16 }} />
      </button>
      <div className="count">{count}</div>
      <button onClick={(e) => onAddClick(e)} className="add control" aria-label="Increment quantity">
        <AddRoundedIcon sx={{ fontSize: 16 }} />
      </button>
    </QuantityControlContainer>
  );
};
